import type { ParsedSipMessage } from 'Modules/softphone/utils/sipTrace/index.types';

export type { ParsedSipMessage } from 'Modules/softphone/utils/sipTrace/index.types';

/** Arrow per direction, matching the notation already used across the panel. */
const ARROW = {
  incoming: '←',
  outgoing: '→',
} as const;

/**
 * How much of a message is kept.
 *
 * An INVITE with SDP is a couple of kilobytes and the buffer holds hundreds of
 * events, so the whole wire is not worth keeping in memory (or in a downloaded
 * debug bundle). The start line, headers and the beginning of the body are what
 * a reader needs; the tail of a long SDP is not.
 */
const MAX_RAW_LENGTH = 4000;

/**
 * SDP lines that are session credentials, blanked rather than dropped so the
 * body keeps its shape and a reader can see something was removed.
 *
 * The same three the debug report redacts from the negotiated SDP — a traced
 * INVITE or 200 OK carries that SDP in its body, so redacting it there and not
 * here would put the credentials back into the very file that promises they
 * were removed.
 */
const REDACTED_SDP_ATTRIBUTES = ['ice-ufrag', 'ice-pwd', 'fingerprint'];

/** Digest credentials never need to leave the browser inside a debug bundle. */
const redactCredentials = (raw: string): string =>
  raw
    .replace(/(response\s*=\s*")[^"]*(")/gi, '$1[redacted]$2')
    .split('\n')
    .map((line) => {
      const attribute = REDACTED_SDP_ATTRIBUTES.find((name) =>
        line.startsWith(`a=${name}:`)
      );

      if (!attribute) {
        return line;
      }

      // The message is CRLF-terminated; keep whatever this line ended with.
      return `a=${attribute}:[redacted]${line.endsWith('\r') ? '\r' : ''}`;
    })
    .join('\n');

const truncate = (raw: string): string =>
  raw.length > MAX_RAW_LENGTH
    ? `${raw.slice(0, MAX_RAW_LENGTH)}\n… truncated`
    : raw;

/**
 * Read a header value, accepting the compact form SIP allows.
 * Header names are case-insensitive, hence the lowercased comparison.
 */
const readHeader = (
  lines: string[],
  name: string,
  compactName?: string
): string | undefined => {
  const wanted = [name.toLowerCase(), compactName?.toLowerCase()].filter(
    (candidate): candidate is string => Boolean(candidate)
  );

  const line = lines.find((candidate) => {
    const separator = candidate.indexOf(':');
    if (separator === -1) {
      return false;
    }

    return wanted.includes(candidate.slice(0, separator).trim().toLowerCase());
  });

  return line?.slice(line.indexOf(':') + 1).trim() || undefined;
};

const RESPONSE_START_LINE = /^SIP\/2\.0\s+(\d{3})\s*(.*)$/;
const REQUEST_START_LINE = /^([A-Za-z]+)\s+(\S+)\s+SIP\/2\.0$/;

/**
 * Turn a raw SIP message into something the timeline can show.
 *
 * Lives next to the transport rather than inside it because this is the only
 * place that knows the panel's notation, and because a parser is worth testing
 * on strings instead of through a WebSocket.
 */
export const parseSipMessage = (
  raw: string,
  direction: 'incoming' | 'outgoing'
): ParsedSipMessage => {
  const arrow = ARROW[direction];
  const trimmed = raw.trim();

  if (!trimmed) {
    return { summary: `keep-alive ${arrow}`, raw: '', isKeepAlive: true };
  }

  const safeRaw = truncate(redactCredentials(raw));
  const [startLine = '', ...headers] = trimmed.split(/\r?\n/);

  const sipCallId = readHeader(headers, 'Call-ID', 'i');
  const cseq = readHeader(headers, 'CSeq');
  const cseqMethod = cseq?.split(/\s+/)[1];

  const response = RESPONSE_START_LINE.exec(startLine);
  if (response) {
    const statusCode = Number(response[1]);
    const reason = response[2].trim();
    const status = [response[1], reason].filter(Boolean).join(' ');

    return {
      // `200 OK ← INVITE`: the response first, then what it answers.
      summary: [status, arrow, cseqMethod].filter(Boolean).join(' '),
      method: cseqMethod,
      statusCode,
      sipCallId,
      cseq,
      raw: safeRaw,
      isKeepAlive: false,
    };
  }

  const request = REQUEST_START_LINE.exec(startLine);
  if (request) {
    const method = request[1].toUpperCase();

    return {
      summary: `${method} ${arrow}`,
      method,
      sipCallId,
      cseq,
      raw: safeRaw,
      isKeepAlive: false,
    };
  }

  // Not a start line we recognise. Still recorded: a malformed message on the
  // wire is exactly the kind of thing this timeline exists to show.
  return {
    summary: `unparsed SIP ${arrow}`,
    sipCallId,
    cseq,
    raw: safeRaw,
    isKeepAlive: false,
  };
};
